import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ReplayIcon from "@mui/icons-material/Replay";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import BlockIcon from "@mui/icons-material/Block";
import StopCircleIcon from "@mui/icons-material/StopCircle";
import { useDispatch, useSelector } from "react-redux";
import {
  getMediaCollectionsSelector,
  GetCollectionsAction,
} from "../../../store/reducers/cmsReducer";
import type { CMSCollection } from "@client/models/CMS/cmsCollection";
import {
  GetContentChannels,
  selectContentChannels,
} from "../../../store/reducers/contentChannelReducer";
import { bulkIngestService } from "@client/http/bulkIngest.service";
import { IncidentList } from "./IncidentList";
import type {
  IBulkIngestJobResponse,
  IBulkIngestItemResponse,
  BulkIngestJobStatus,
} from "@nendaTypes/NendaTypes";
import { isActiveIngestPhase } from "@shared/ingestPhases";

/** Grey and filled, so a deliberate stop never reads as the red of a failure. */
const CANCELLED_CHIP_SX = {
  bgcolor: "grey.600",
  color: "common.white",
  "& .MuiChip-icon": { color: "common.white" },
} as const;

function getRelativeUploadPath(file: File): string {
  const fullPath = (file as any).webkitRelativePath || file.name;
  return fullPath.includes("/")
    ? fullPath.split("/").slice(1).join("/")
    : fullPath;
}

function isIgnoredUploadFile(file: File): boolean {
  const relativePath = getRelativeUploadPath(file);
  const pathSegments = relativePath.split("/").filter(Boolean);

  // Ignore hidden files/folders and macOS resource-fork artifacts.
  return pathSegments.some(
    (segment) => segment.startsWith(".") || segment.startsWith("._")
  );
}

/**
 * Derive the effective display status for an item.
 * If the item has a linked ingest job, use the job phase as truth.
 * Otherwise fall back to the static item.status (pending / prepare-time failure).
 */
function getItemDisplayStatus(item: IBulkIngestItemResponse): {
  label: string;
  color: "default" | "info" | "success" | "error" | "warning";
  icon?: React.ReactElement;
  progress?: number;
  error?: string;
  sx?: Record<string, unknown>;
} {
  if (item.ingestJob) {
    const { phase, progress, error } = item.ingestJob.currentPhase;
    switch (phase) {
      case "queued":
        return { label: "Queued", color: "default" };
      case "transcoding":
        return {
          label: progress != null ? `Transcoding ${progress}%` : "Transcoding",
          color: "info",
          progress,
        };
      case "assembly":
        return { label: "Assembly", color: "info" };
      case "ingesting":
        return { label: "Ingesting", color: "info" };
      case "publishing":
        return { label: "Publishing", color: "info" };
      case "completed":
        return {
          label: "Completed",
          color: "success",
          icon: <CheckCircleIcon />,
        };
      case "failed":
        return {
          label: "Failed",
          color: "error",
          icon: <ErrorIcon />,
          error: error || undefined,
        };
      case "cancelled":
        return {
          label: "Cancelled",
          color: "default",
          icon: <BlockIcon />,
          sx: CANCELLED_CHIP_SX,
        };
      default:
        return { label: phase, color: "default" };
    }
  }

  // No ingest job yet — use item.status
  switch (item.status) {
    case "pending":
      return { label: "Pending", color: "default" };
    case "prepared":
      return { label: "Prepared", color: "info" };
    case "completed":
      return {
        label: "Completed",
        color: "success",
        icon: <CheckCircleIcon />,
      };
    case "failed":
      return {
        label: "Failed",
        color: "error",
        icon: <ErrorIcon />,
        error: item.error || undefined,
      };
    case "cancelled":
      return {
        label: "Cancelled",
        color: "default",
        icon: <BlockIcon />,
        sx: CANCELLED_CHIP_SX,
      };
    default:
      return { label: item.status, color: "default" };
  }
}

function formatDate(date?: Date | string): string {
  if (!date) return "—";
  return new Date(date).toLocaleString("sv-SE");
}

function formatDuration(ms: number): string {
  if (ms < 0) return "0m";
  const totalSeconds = Math.floor(ms / 1000);
  const days = Math.floor(totalSeconds / 86_400);
  const hours = Math.floor((totalSeconds % 86_400) / 3_600);
  const minutes = Math.floor((totalSeconds % 3_600) / 60);

  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

function formatTimeInCurrentPhase(item: IBulkIngestItemResponse): string {
  const phaseStartedAt = item.ingestJob?.currentPhase?.startedAt;
  if (!phaseStartedAt) return "—";

  const startMs = new Date(phaseStartedAt).getTime();
  if (Number.isNaN(startMs)) return "—";

  const phaseCompletedAt = item.ingestJob?.currentPhase?.completedAt;
  const endMs = phaseCompletedAt
    ? new Date(phaseCompletedAt).getTime()
    : Date.now();

  if (Number.isNaN(endMs)) return "—";
  return formatDuration(endMs - startMs);
}

function isTerminalStatus(status: BulkIngestJobStatus): boolean {
  return ["completed", "failed", "cancelled"].includes(status);
}

/** Mirrors the server's own guard. */
const CANCELLABLE_STATUSES: BulkIngestJobStatus[] = [
  "preparing",
  "preparing_failed",
  "processing",
];

function formatBulkJobStatus(status: BulkIngestJobStatus): string {
  switch (status) {
    case "preparing_failed":
      return "Preparing failed";
    case "cancelling":
      return "Cancelling…";
    default:
      return status.charAt(0).toUpperCase() + status.slice(1);
  }
}

interface CancellationSummary {
  /** Not started by the transcoder yet, so the orchestrator can drop them. */
  queued: number;
  /** Already with the transcoder or downstream of it. These run to the end. */
  inFlight: number;
  published: number;
  failed: number;
  cancelled: number;
}

/**
 * What a cancellation would do to each item. `inFlight` items cannot be aborted and still publish,
 * which is the part of cancelling a bulk job that nobody expects.
 */
function summariseCancellation(
  items: IBulkIngestItemResponse[]
): CancellationSummary {
  const summary: CancellationSummary = {
    queued: 0,
    inFlight: 0,
    published: 0,
    failed: 0,
    cancelled: 0,
  };

  for (const item of items) {
    const phase = item.ingestJob?.currentPhase?.phase;

    if (phase === "completed" || item.status === "completed") {
      summary.published++;
    } else if (phase === "failed" || item.status === "failed") {
      summary.failed++;
    } else if (phase === "cancelled" || item.status === "cancelled") {
      summary.cancelled++;
    } else if (phase && phase !== "queued" && isActiveIngestPhase(phase)) {
      summary.inFlight++;
    } else {
      summary.queued++;
    }
  }

  return summary;
}

function pluralise(count: number, singular: string): string {
  return `${count} ${singular}${count === 1 ? "" : "s"}`;
}

const POLL_INTERVAL_MS = 10_000; // 10 seconds

const BulkIngestDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [job, setJob] = useState<IBulkIngestJobResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [blobFiles, setBlobFiles] = useState<string[]>([]);
  const [validating, setValidating] = useState(false);
  const [starting, setStarting] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const collections = useSelector(
    getMediaCollectionsSelector
  ) as CMSCollection[];
  const dispatch = useDispatch();
  const [selectedEntitlements, setSelectedEntitlements] = useState<string[]>(
    []
  );
  const contentChannels = useSelector(selectContentChannels);
  const [selectedContentChannels, setSelectedContentChannels] = useState<
    string[]
  >([]);
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const toggleItemExpanded = (index: number) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedItems(newExpanded);
  };

  const fetchFiles = useCallback(() => {
    if (!id) return;
    bulkIngestService.listFiles(id).subscribe({
      next: (data: { files: string[] }) => setBlobFiles(data.files),
      error: (err: any) => {
        console.error("Failed to list files:", err?.response || err);
      },
    });
  }, [id]);

  const fetchJob = useCallback(
    (isPolling = false) => {
      if (!id) return;
      bulkIngestService.getById(id).subscribe({
        next: (data: IBulkIngestJobResponse) => {
          setJob(data);
          setLoading(false);
          setError(null);
        },
        error: (err: any) => {
          // Only show error on initial load, not during polling
          if (!isPolling) {
            setError(err?.response?.message || "Failed to load job");
          }
          setLoading(false);
        },
      });
    },
    [id]
  );

  // Load collections for entitlement selector
  useEffect(() => {
    if (collections.length === 0) {
      dispatch(GetCollectionsAction());
    }
  }, [collections.length, dispatch]);

  useEffect(() => {
    if (contentChannels.length === 0) {
      dispatch(GetContentChannels());
    }
  }, [contentChannels.length, dispatch]);

  // Pre-populate entitlements from globalMetadata when job becomes validated
  useEffect(() => {
    if (job?.status === "validated") {
      setSelectedEntitlements(job.globalMetadata?.entitlements ?? []);
      setSelectedContentChannels(job.globalMetadata?.contentChannels ?? []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job?.status]);

  // Initial fetch
  useEffect(() => {
    fetchJob();
    fetchFiles();
  }, [fetchJob, fetchFiles]);

  // Polling for active jobs
  const jobStatus = job?.status;

  useEffect(() => {
    if (!jobStatus) {
      if (pollRef.current) clearInterval(pollRef.current);
      return;
    }

    // Check for active ingest items inside the effect, not as a dependency
    const hasActiveIngestItems = job?.items?.some((item) =>
      isActiveIngestPhase(item.ingestJob?.currentPhase.phase)
    );

    const shouldPoll =
      ["preparing", "processing", "cancelling"].includes(jobStatus) ||
      hasActiveIngestItems;

    if (shouldPoll) {
      pollRef.current = setInterval(() => fetchJob(true), POLL_INTERVAL_MS);
    } else if (isTerminalStatus(jobStatus)) {
      if (pollRef.current) clearInterval(pollRef.current);
    }

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [jobStatus, fetchJob, job?.items]);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = e.target.files;
    if (!fileList || fileList.length === 0 || !id) return;

    // Copy files before resetting input to allow reupload on failure
    const files = Array.from(fileList);
    const uploadFiles = files.filter((file) => !isIgnoredUploadFile(file));
    e.target.value = "";

    if (uploadFiles.length === 0) {
      setError("No valid files found in selected folder.");
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setError(null);

    const totalFiles = uploadFiles.length;
    let uploaded = 0;
    const failed: string[] = [];

    for (let i = 0; i < uploadFiles.length; i++) {
      const file = uploadFiles[i];
      const relativePath = getRelativeUploadPath(file);

      try {
        const uploadUrlResponse = await new Promise<any>((resolve, reject) => {
          bulkIngestService
            .getUploadUrl(id, relativePath, file.type || undefined)
            .subscribe({ next: resolve, error: reject });
        });

        const sasUrl = uploadUrlResponse.uploadUrl || uploadUrlResponse.url;
        if (!sasUrl) {
          // SAS generation failed — systemic issue, abort
          setError("Could not start upload. Please try again.");
          setUploading(false);
          setUploadProgress(0);
          if (uploaded > 0) fetchFiles();
          return;
        }

        const putResponse = await fetch(sasUrl, {
          method: "PUT",
          headers: {
            "x-ms-blob-type": "BlockBlob",
            "Content-Type": file.type || "application/octet-stream",
          },
          body: file,
        });

        if (!putResponse.ok) {
          // Auth/permission failures are systemic — abort immediately
          if (putResponse.status === 403 || putResponse.status === 401) {
            const body = await putResponse.text();
            setError(
              `Storage rejected the upload (${putResponse.status}). ${body}`
            );
            setUploading(false);
            setUploadProgress(0);
            if (uploaded > 0) fetchFiles();
            return;
          }
          failed.push(relativePath);
          continue;
        }

        uploaded++;
      } catch (err: any) {
        // getUploadUrl failure is systemic (function down or auth expired)
        if (uploaded === 0 && failed.length === 0) {
          setError(
            err?.response?.error || "Could not start upload. Please try again."
          );
          setUploading(false);
          setUploadProgress(0);
          return;
        }
        failed.push(relativePath);
      }

      setUploadProgress(
        Math.round(((uploaded + failed.length) / totalFiles) * 100)
      );
    }

    setUploading(false);
    if (failed.length > 0) {
      setError(`${failed.length} of ${totalFiles} files failed to upload`);
    }
    fetchJob();
    fetchFiles();
  };

  const handleValidate = () => {
    if (!id) return;
    setValidating(true);
    setError(null);
    bulkIngestService.validate(id).subscribe({
      next: (result: any) => {
        setJob(result.job);
        setValidating(false);
      },
      error: (err: any) => {
        setError(err?.response?.error || err?.message || "Validation failed");
        setValidating(false);
      },
    });
  };

  const handleStart = () => {
    if (!id) return;
    setStarting(true);
    setError(null);
    bulkIngestService
      .start(id, {
        entitlements: selectedEntitlements.length
          ? selectedEntitlements
          : undefined,
        contentChannels: selectedContentChannels.length
          ? selectedContentChannels
          : undefined,
      })
      .subscribe({
        next: (data: IBulkIngestJobResponse) => {
          setJob(data);
          setStarting(false);
        },
        error: (err: any) => {
          setError(err?.response?.error || err?.message || "Start failed");
          setStarting(false);
        },
      });
  };

  const handleRetry = () => {
    if (!id) return;
    setStarting(true);
    setError(null);

    bulkIngestService.retry(id).subscribe({
      next: (data: IBulkIngestJobResponse) => {
        setJob(data);
        setStarting(false);
      },
      error: (err: any) => {
        setError(err?.response?.error || err?.message || "Retry failed");
        fetchJob(true);
        setStarting(false);
      },
    });
  };

  const handleCancel = () => {
    if (!id) return;
    setCancelling(true);
    setError(null);
    bulkIngestService.cancel(id).subscribe({
      next: (data: IBulkIngestJobResponse) => {
        setJob(data);
        setCancelling(false);
        setCancelDialogOpen(false);
      },
      error: (err: any) => {
        setError(err?.response?.message || err?.message || "Cancel failed");
        setCancelling(false);
        setCancelDialogOpen(false);
        fetchJob(true);
      },
    });
  };

  const handleClose = () => {
    if (!id) return;
    if (
      !window.confirm(
        "Close this job? This will delete failed assets and cannot be undone."
      )
    ) {
      return;
    }
    setError(null);
    bulkIngestService.close(id).subscribe({
      next: (data: IBulkIngestJobResponse) => {
        setJob(data);
      },
      error: (err: any) => {
        setError(err?.response?.error || err?.message || "Close failed");
      },
    });
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
      </Box>
    );
  }

  if (!job) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error || "Job not found"}</Alert>
      </Box>
    );
  }

  const cancelledItems = job.cancelledItems ?? 0;

  const progressPercent =
    job.totalItems > 0
      ? Math.min(
          100,
          Math.round(
            ((job.completedItems + job.failedItems + cancelledItems) /
              job.totalItems) *
              100
          )
        )
      : 0;

  const hasFailedItems =
    job.items?.some((item) => {
      if (item.ingestJob?.currentPhase.phase) {
        return item.ingestJob.currentPhase.phase === "failed";
      }
      return item.status === "failed";
    }) ?? false;

  const canUpload = job.status === "created" && blobFiles.length === 0;
  const canValidate = job.status === "created" && blobFiles.length > 0;
  const canStart = job.status === "validated";
  const canRetry =
    (job.status === "preparing_failed" || job.failedItems > 0) &&
    hasFailedItems;
  const canClose =
    ["completed", "failed", "preparing_failed"].includes(job.status) &&
    hasFailedItems;
  const isCancelling = job.status === "cancelling";
  const showCancel = CANCELLABLE_STATUSES.includes(job.status) || isCancelling;
  const cancellation = summariseCancellation(job.items ?? []);

  const effectiveStatus = (): BulkIngestJobStatus => {
    if (job.status === "completed" && !hasFailedItems) {
      return "completed";
    }
    return job.status;
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("../")}
          size="small"
        >
          Back
        </Button>
        <Typography variant="h5">{job.name}</Typography>
        <Chip
          label={formatBulkJobStatus(effectiveStatus())}
          color={
            (
              {
                created: "default",
                validated: "info",
                preparing: "warning",
                preparing_failed: "error",
                processing: "warning",
                completed: "success",

                failed: "error",
                cancelling: "warning",
                cancelled: "default",
              } as Record<BulkIngestJobStatus, any>
            )[effectiveStatus()]
          }
          size="small"
        />
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Job info */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack spacing={1}>
          {job.description && (
            <Typography variant="body2" color="text.primary">
              {job.description}
            </Typography>
          )}
          <Typography variant="body2">
            Created by {job.createdBy} on {formatDate(job.createdAt)}
          </Typography>
          {job.startedAt && (
            <Typography variant="body2">
              Started: {formatDate(job.startedAt)}
            </Typography>
          )}
          {job.completedAt && (
            <Typography variant="body2">
              Completed: {formatDate(job.completedAt)}
            </Typography>
          )}
        </Stack>
      </Paper>

      {job.error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {job.error}
        </Alert>
      )}

      {isCancelling && (
        <Alert severity="info" icon={<StopCircleIcon />} sx={{ mb: 2 }}>
          Cancellation requested. Queued items are dropped within about 30
          seconds. The item already transcoding finishes and publishes.
        </Alert>
      )}

      {/* Progress bar for active jobs */}
      {["preparing", "processing", "cancelling", "cancelled"].includes(
        job.status
      ) && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Progress: {job.completedItems + job.failedItems + cancelledItems} /{" "}
            {job.totalItems} items ({progressPercent}%)
          </Typography>
          <LinearProgress
            variant="determinate"
            value={progressPercent}
            sx={{ height: 8, borderRadius: 4 }}
          />
          <Stack direction="row" spacing={3} sx={{ mt: 1 }}>
            <Typography variant="caption" color="success.main">
              {job.completedItems} completed
            </Typography>
            {job.failedItems > 0 && (
              <Typography variant="caption" color="error.main">
                {job.failedItems} failed
              </Typography>
            )}
            {cancelledItems > 0 && (
              <Typography variant="caption" sx={{ color: "grey.600" }}>
                {cancelledItems} cancelled
              </Typography>
            )}
            <Typography variant="caption" color="text.primary">
              {job.preparedItems} prepared
            </Typography>
          </Stack>
        </Paper>
      )}

      {/* Job incidents (warnings/issues during processing) */}
      {job.incidents && job.incidents.length > 0 && (
        <Paper sx={{ p: 2, mb: 3, bgcolor: "warning.lighter" }}>
          <Typography variant="subtitle2" sx={{ mb: 1, color: "warning.dark" }}>
            ⚠ Issues During Processing ({job.incidents.length})
          </Typography>
          <IncidentList incidents={job.incidents} />
        </Paper>
      )}

      {/* Actions */}
      <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
        {canUpload && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              // @ts-expect-error — webkitdirectory is not in standard types
              webkitdirectory=""
              multiple
              hidden
              onChange={handleFileSelect}
            />
            <Button
              variant="contained"
              startIcon={<CloudUploadIcon />}
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              {uploading ? `Uploading... ${uploadProgress}%` : "Upload Package"}
            </Button>
          </>
        )}
        {canValidate && (
          <Button
            variant="contained"
            onClick={handleValidate}
            disabled={validating}
          >
            {validating ? "Validating..." : "Validate Package"}
          </Button>
        )}
        {canStart && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<PlayArrowIcon />}
            onClick={handleStart}
            disabled={starting}
          >
            Start Ingest
          </Button>
        )}
        {canRetry && (
          <Button
            variant="outlined"
            color="warning"
            startIcon={<ReplayIcon />}
            onClick={handleRetry}
            disabled={starting}
          >
            Retry Failed Items
          </Button>
        )}
        {showCancel && (
          <Button
            variant="outlined"
            color="error"
            startIcon={<StopCircleIcon />}
            onClick={() => setCancelDialogOpen(true)}
            disabled={isCancelling || cancelling}
          >
            {isCancelling ? "Cancelling…" : "Cancel Job"}
          </Button>
        )}
        {canClose && (
          <Button variant="outlined" color="error" onClick={handleClose}>
            Close Job
          </Button>
        )}
      </Stack>

      <Dialog
        open={cancelDialogOpen}
        onClose={() => !cancelling && setCancelDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Cancel “{job.name}”?</DialogTitle>
        <DialogContent>
          <DialogContentText component="div" color="text.primary">
            <Stack spacing={1}>
              <Typography variant="body2">
                {pluralise(cancellation.queued, "queued item")} will be
                cancelled.
              </Typography>
              {cancellation.inFlight > 0 && (
                <Typography variant="body2">
                  {pluralise(cancellation.inFlight, "item")} currently
                  transcoding will finish and publish —{" "}
                  {cancellation.inFlight === 1 ? "it" : "they"} cannot be
                  aborted.
                </Typography>
              )}
              {cancellation.published > 0 && (
                <Typography variant="body2">
                  {pluralise(cancellation.published, "already-published item")}{" "}
                  {cancellation.published === 1 ? "is" : "are"} unaffected.
                </Typography>
              )}
              {cancellation.failed > 0 && (
                <Typography variant="body2">
                  {pluralise(cancellation.failed, "failed item")}{" "}
                  {cancellation.failed === 1 ? "stays" : "stay"} failed.
                </Typography>
              )}
              <Typography variant="body2">
                Cancelled items keep their uploaded files and can be re-queued
                later.
              </Typography>
            </Stack>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setCancelDialogOpen(false)}
            disabled={cancelling}
          >
            Keep running
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleCancel}
            disabled={cancelling}
          >
            {cancelling ? "Cancelling…" : "Cancel job"}
          </Button>
        </DialogActions>
      </Dialog>

      {uploading && (
        <LinearProgress
          variant="determinate"
          value={uploadProgress}
          sx={{ mb: 2 }}
        />
      )}

      {/* Pre-start panel: global metadata summary + entitlement selector */}
      {canStart && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1.5 }}>
            Global metadata
          </Typography>
          {job.globalMetadata ? (
            <Stack spacing={0.5} sx={{ mb: 2 }}>
              {job.globalMetadata.provider && (
                <Typography variant="body2">
                  <strong>Provider:</strong> {job.globalMetadata.provider}
                </Typography>
              )}
              {job.globalMetadata.tags &&
                job.globalMetadata.tags.length > 0 && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      flexWrap: "wrap",
                    }}
                  >
                    <Typography variant="body2">
                      <strong>Tags:</strong>
                    </Typography>
                    {job.globalMetadata.tags.map((tag) => (
                      <Chip key={tag} label={tag} size="small" />
                    ))}
                  </Box>
                )}
              {job.globalMetadata.entitlements &&
                job.globalMetadata.entitlements.length > 0 && (
                  <Typography variant="body2" color="text.disabled">
                    Entitlements pre-populated from metadata file — edit below.
                  </Typography>
                )}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.disabled" sx={{ mb: 2 }}>
              No global metadata in package.
            </Typography>
          )}
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Entitlements
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Select media collections to assign to all items after ingest. Merges
            with any item-specific entitlements. Collections from the metadata
            file are locked and cannot be removed.
          </Typography>
          <Autocomplete
            multiple
            options={collections}
            disableCloseOnSelect
            getOptionLabel={(option) => option.name || option._id}
            value={collections.filter((c) =>
              selectedEntitlements.includes(c._id)
            )}
            onChange={(_event, newValue) => {
              const lockedIds = job.globalMetadata?.entitlements ?? [];
              // Always keep locked ones regardless of what the user removed
              const merged = Array.from(
                new Set([...lockedIds, ...newValue.map((c) => c._id)])
              );
              setSelectedEntitlements(merged);
            }}
            renderTags={(tagValue, getTagProps) =>
              tagValue.map((option, index) => {
                const isLocked = (
                  job.globalMetadata?.entitlements ?? []
                ).includes(option._id);
                const { key, onDelete, ...tagProps } = getTagProps({ index });
                return (
                  <Chip
                    key={key}
                    {...tagProps}
                    label={option.name || option._id}
                    onDelete={isLocked ? undefined : onDelete}
                    title={isLocked ? "From metadata file" : undefined}
                  />
                );
              })
            }
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox
                  icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                  checkedIcon={<CheckBoxIcon fontSize="small" />}
                  style={{ marginRight: 8 }}
                  checked={selected}
                />
                {option.name || option._id}
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                label="Media Collections"
                placeholder="Search collections…"
              />
            )}
            sx={{ maxWidth: 500 }}
          />
          <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
            Content channels
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Each item is added to the end of these channels once it is published
            and its entitlements are applied. Items are added in the order they
            finish, not in package order.
          </Typography>
          <Autocomplete
            multiple
            options={contentChannels}
            disableCloseOnSelect
            getOptionLabel={(option) => option.name || String(option._id)}
            isOptionEqualToValue={(option, value) => option._id === value._id}
            value={contentChannels.filter((c) =>
              selectedContentChannels.includes(String(c._id))
            )}
            onChange={(_event, newValue) =>
              setSelectedContentChannels(newValue.map((c) => String(c._id)))
            }
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox
                  icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                  checkedIcon={<CheckBoxIcon fontSize="small" />}
                  style={{ marginRight: 8 }}
                  checked={selected}
                />
                {option.name || String(option._id)}
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                label="Content Channels"
                placeholder="Search channels…"
              />
            )}
            sx={{ maxWidth: 500 }}
          />
        </Paper>
      )}

      {/* Uploaded files (shown before validation) */}
      {blobFiles.length > 0 && job.status === "created" && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Uploaded files ({blobFiles.length})
          </Typography>
          <Box
            component="ul"
            sx={{ m: 0, pl: 2, maxHeight: 200, overflow: "auto" }}
          >
            {blobFiles.map((file) => (
              <li key={file}>
                <Typography variant="body2" fontFamily="monospace">
                  {file}
                </Typography>
              </li>
            ))}
          </Box>
        </Paper>
      )}

      <Divider sx={{ mb: 3 }} />

      {/* Items table */}
      {job.items && job.items.length > 0 && (
        <>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Items ({job.items.length})
          </Typography>
          <TableContainer component={Paper} sx={{ maxHeight: 500 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell style={{ width: 40 }} />
                  <TableCell>#</TableCell>
                  <TableCell>Video</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Phase Started</TableCell>
                  <TableCell>Time In Phase</TableCell>
                  <TableCell>Asset ID</TableCell>
                  <TableCell>Error</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {job.items.map(
                  (item: IBulkIngestItemResponse, index: number) => {
                    const display = getItemDisplayStatus(item);
                    const isExpanded = expandedItems.has(index);
                    const hasIncidents =
                      item.ingestJob?.incidents &&
                      item.ingestJob.incidents.length > 0;

                    return (
                      <React.Fragment key={index}>
                        <TableRow>
                          <TableCell style={{ width: 40 }} align="center">
                            {hasIncidents && (
                              <IconButton
                                size="small"
                                onClick={() => toggleItemExpanded(index)}
                              >
                                {isExpanded ? (
                                  <ExpandMoreIcon fontSize="small" />
                                ) : (
                                  <ChevronRightIcon fontSize="small" />
                                )}
                              </IconButton>
                            )}
                          </TableCell>
                          <TableCell>{index + 1}</TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              noWrap
                              sx={{ maxWidth: 300 }}
                            >
                              {item.videoPath}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={display.label}
                              color={display.color}
                              size="small"
                              icon={display.icon}
                              sx={display.sx}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption">
                              {formatDate(
                                item.ingestJob?.currentPhase?.startedAt
                              )}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption">
                              {formatTimeInCurrentPhase(item)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {item.assetId ? (
                              <Typography
                                variant="caption"
                                fontFamily="monospace"
                                component="a"
                                href={`/admin/metadata/vod/${item.assetId}`}
                                sx={{
                                  color: "primary.main",
                                  textDecoration: "none",
                                  "&:hover": { textDecoration: "underline" },
                                }}
                              >
                                {item.assetId}
                              </Typography>
                            ) : (
                              <Typography
                                variant="caption"
                                fontFamily="monospace"
                              >
                                —
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            {(display.error || item.error) && (
                              <Typography
                                variant="caption"
                                color="error"
                                sx={{ maxWidth: 200 }}
                                noWrap
                                title={display.error || item.error}
                              >
                                {display.error || item.error}
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>

                        {/* Expanded incidents row */}
                        {isExpanded &&
                          hasIncidents &&
                          item.ingestJob?.incidents && (
                            <TableRow>
                              <TableCell colSpan={8} sx={{ p: 0 }}>
                                <Box sx={{ p: 2, bgcolor: "warning.lighter" }}>
                                  <Typography
                                    variant="subtitle2"
                                    sx={{
                                      mb: 1,
                                      color: "warning.dark",
                                      fontWeight: 600,
                                    }}
                                  >
                                    ⚠ Incidents (
                                    {item.ingestJob.incidents.length})
                                  </Typography>
                                  <IncidentList
                                    incidents={item.ingestJob.incidents}
                                  />
                                </Box>
                              </TableCell>
                            </TableRow>
                          )}
                      </React.Fragment>
                    );
                  }
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
};

export default BulkIngestDetailPage;
